import React from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  IconButton,
  Divider,
  Grid,
  Alert
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

function Cart({ cartItems, removeFromCart, setCartItems }) {
  const calculateTotal = () => {
    return cartItems.reduce((total, item) => total + (item.price * (item.quantity || 1)), 0).toFixed(2);
  };

  const calculateSavings = () => {
    return cartItems.reduce((total, item) => {
      const discount = item.originalPrice ? (item.originalPrice - item.price) * (item.quantity || 1) : 0;
      return total + discount;
    }, 0).toFixed(2);
  };

  const handleCheckout = () => {
    alert('Checkout in sviluppo. Grazie per aver provato ProvaMi!');
  };

  const clearCart = () => {
    setCartItems([]);
    localStorage.removeItem('cartItems');
  };

  if (cartItems.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 5 }}>
        <ShoppingBagIcon sx={{ fontSize: 100, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h5" gutterBottom>
          Il tuo carrello è vuoto
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          Aggiungi alcuni prodotti per iniziare lo shopping!
        </Typography>
        <Button
          variant="contained"
          component={Link}
          to="/products"
          startIcon={<ArrowBackIcon />}
        >
          Continua lo Shopping
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Carrello ({cartItems.length} {cartItems.length === 1 ? 'articolo' : 'articoli'})
      </Typography>

      <Grid container spacing={3}>
        {/* Lista prodotti */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Paper sx={{ p: 2 }}>
            <List>
              {cartItems.map((item, index) => (
                <React.Fragment key={item.cartId || `${item.id}-${index}`}>
                  <ListItem>
                    <ListItemAvatar>
                      <Avatar
                        src={item.imageUrl}
                        variant="rounded"
                        sx={{ width: 80, height: 80, mr: 2 }}
                      />
                    </ListItemAvatar>

                    <ListItemText
                      primary={
                        <Typography variant="h6">
                          {item.name}
                        </Typography>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {item.category} | {item.brand || 'Piazza Italia'}
                          </Typography>
                          {item.selectedSize && (
                            <Typography variant="body2">
                              Taglia: {item.selectedSize}
                            </Typography>
                          )}
                          {item.selectedColor && (
                            <Typography variant="body2">
                              Colore: {item.selectedColor}
                            </Typography>
                          )}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                            <Typography variant="h6" color="primary">
                              €{item.price}
                            </Typography>
                            {item.originalPrice > item.price && (
                              <Typography
                                variant="body2"
                                sx={{ textDecoration: 'line-through', color: 'text.secondary' }}
                              >
                                €{item.originalPrice}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      }
                    />

                    <IconButton
                      edge="end"
                      aria-label="delete"
                      onClick={() => removeFromCart(item.id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItem>
                  {index < cartItems.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>

            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
              <Button
                variant="outlined"
                component={Link}
                to="/products"
                startIcon={<ArrowBackIcon />}
              >
                Continua lo Shopping
              </Button>
              <Button
                variant="outlined"
                color="error"
                onClick={clearCart}
              >
                Svuota Carrello
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* Riepilogo ordine */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper sx={{ p: 3, position: 'sticky', top: 90 }}>
            <Typography variant="h6" gutterBottom>
              Riepilogo Ordine
            </Typography>

            <Box sx={{ my: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography>Subtotale:</Typography>
                <Typography>€{calculateTotal()}</Typography>
              </Box>

              {parseFloat(calculateSavings()) > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography color="success.main">Risparmi:</Typography>
                  <Typography color="success.main">-€{calculateSavings()}</Typography>
                </Box>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography>Spedizione:</Typography>
                <Typography color="success.main">GRATIS</Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6">Totale:</Typography>
                <Typography variant="h6" color="primary">
                  €{calculateTotal()}
                </Typography>
              </Box>
            </Box>

            {parseFloat(calculateSavings()) > 0 && (
              <Alert severity="success" sx={{ mb: 2 }}>
                Hai risparmiato €{calculateSavings()}!
              </Alert>
            )}

            <Button
              variant="contained"
              fullWidth
              size="large"
              onClick={handleCheckout}
            >
              Procedi al Checkout
            </Button>

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
              * Spedizione gratuita per ordini superiori a €30
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default Cart;